import { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';

import { ReactComponent as LeftArrowSvg } from '../../image/LeftArrow.svg';
import SettingTimeSelectBox from './NewSettingTimeSelectBox';

const SettingCountInput = forwardRef((props, ref) => {
  const { text, onCommit } = props;
  const { t, languageMode } = useLanguage();

  const [dateVal, setDateVal] = useState(0);
  const [unit, setUnit] = useState({ name: 'min', label: t('system.min') });

  const InputRef = useRef();
  // 최근 commit 성공한 (값, 단위) — 실패 시 롤백 기준 //
  const lastCommittedRef = useRef({ value: 0, unitName: 'min' });
  // input focus 시점 dateVal — blur 시 변경 판정용 //
  const focusSnapshotRef = useRef(0);

  const options = [
    { name: 'hour', label: t('system.hour') },
    { name: 'min', label: t('system.min') }
  ];

  // 단위 코드를 분 환산용 곱수로 변환 (day → 60*24, hour → 60, min → 1) //
  function unitMultiplier(name) {
    if (name === 'day') return 60 * 24;
    if (name === 'hour') return 60;
    return 1;
  }

  // 단위 코드별 i18n 라벨 반환 //
  function unitLabel(name) {
    if (name === 'hour') return t('system.hour');
    if (name === 'day') return t('system.day');
    return t('system.min');
  }

  // 값/단위 변경 후 외부에 통보 (parent가 patch 호출) //
  function commit(nextDateVal, nextUnit) {
    if (!onCommit) return;

    const nextSnapshot = { value: nextDateVal, unitName: nextUnit.name };
    // 변경 없음 → skip
    if (nextSnapshot.value === lastCommittedRef.current.value
      && nextSnapshot.unitName === lastCommittedRef.current.unitName) return;

    const prev = lastCommittedRef.current;
    lastCommittedRef.current = nextSnapshot;

    const prevUnitObj = { name: prev.unitName, label: unitLabel(prev.unitName) };

    onCommit(
      { value: nextDateVal * unitMultiplier(nextUnit.name), unit: nextUnit.name },
      () => {
        setDateVal(prev.value);
        setUnit(prevUnitObj);
        lastCommittedRef.current = prev;
      }
    );
  }

  // 언어 변경 시 이미 선택된 단위의 label 재계산 //
  useEffect(() => {
    setUnit(prev => ({ name: prev.name, label: unitLabel(prev.name) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageMode]);

  useImperativeHandle(ref, () => ({
    setValue: (val) => {
      setDateVal(val);
      setUnit({ name: 'min', label: unitLabel('min') });
      lastCommittedRef.current = { value: val, unitName: 'min' };
    },
    setUnit: (val) => {
      let nextName;

      if (val === 'min') nextName = 'min';
      else if (val === 'time') nextName = 'hour';
      else nextName = 'day';

      setUnit({ name: nextName, label: unitLabel(nextName) });
      lastCommittedRef.current = { ...lastCommittedRef.current, unitName: nextName };
    },
    getValue: () => {
      return dateVal * unitMultiplier(unit.name);
    },
    getUnit: () => {
      return unit.name;
    }
  }))

  // 입력값 변경시 //
  function onChangeInput(e) {
    const regex = /[^0-9]/g;
    const result = e.target.value.replace(regex, "");
    setDateVal(result === '' ? 0 : parseInt(result));
  }

  // 입력 포커스시 — blur 시 변경 판정용 스냅샷 //
  function onFocusInput() {
    focusSnapshotRef.current = dateVal;
  }

  // 입력 포커스 해제시 //
  function onBlurInput() {
    if (dateVal === focusSnapshotRef.current) return;
    commit(dateVal, unit);
  }

  // 단위 변경시 즉시 commit //
  function onChangeUnit(nextUnit) {
    setUnit(nextUnit);
    commit(dateVal, nextUnit);
  }

  // 증가 버튼 클릭시 //
  function onClickUp() {
    const next = dateVal + 1;
    setDateVal(next);
    commit(next, unit);
  }

  // 감소 버튼 클릭시 //
  function onClickDown() {
    if (dateVal > 0) {
      const next = dateVal - 1;
      setDateVal(next);
      commit(next, unit);
    }
  }

  return (
    <TopLevelWrapper>
      <Input
        ref={InputRef}
        placeholder={0}
        value={dateVal}
        onChange={onChangeInput}
        onFocus={onFocusInput}
        onBlur={onBlurInput} />
      <ButtonWrapper>
        <Button onClick={onClickUp}><StyledArrow isUp={true} /></Button>
        <Button onClick={onClickDown}><StyledArrow isUp={false} /></Button>
      </ButtonWrapper>
      <SettingTimeSelectBox
        options={options}
        value={unit}
        setValue={onChangeUnit}
        width={72} />
      <UnitText>{text}</UnitText>
    </TopLevelWrapper>
  )
})

export default SettingCountInput;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-top: 8px;

  transition: all 0.3s ease-in-out;
`;

const Input = styled.input`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 61px;
  height: 30px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};

  padding: 0 0 0 8px;
  margin: 0 5px 0 0;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  ::placeholder {
    color: ${props => props.theme.line};
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 4px;
  margin-right: 8px;
`;

const Button = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 30px;
  height: 30px;

  border: 1px solid ${props => props.theme.buttonFill};
  border-radius: 8px;
  background-color: ${props => props.theme.buttonFill};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const StyledArrow = styled(LeftArrowSvg)`
  width: 9.6px;
  height: 17.7px;

  transform: ${props => props.isUp ? 'rotate(90deg)' : 'rotate(-90deg)'};

  & path {
    fill: ${props => props.theme.subText};
  }
`;

const UnitText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 12px;

  color: ${props => props.theme.subText};

  margin: 0 0 0 8px;
`;
